import clsx from "clsx";
import { GetStaticPaths, GetStaticPropsContext } from "next";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";
import { dehydrate, QueryClient } from "react-query";

import { ModelEventEnum } from "@/consts/AnalyticsEvent";
import { useUserContext } from "@/features/auth/contexts/useUserContext";
import { Breadcrumbs } from "@/features/common/components/Breadcrumbs";
import { Button } from "@/features/common/components/Button";
import { CloudinaryImage } from "@/features/common/components/CloudinaryImage";
import { Container } from "@/features/common/components/Container";
import { EllipsisText } from "@/features/common/components/EllipsisText";
import { NotFoundError } from "@/features/common/components/NotFoundError";
import { useAnalytics } from "@/features/common/hooks/useAnalytics";
import { useSeoProps } from "@/features/common/hooks/useSeoProps";
import { SiteLayout } from "@/features/layout/SiteLayout";
import { IGFollowerNumberItem } from "@/features/model/components/IGFollowerNumberItem";
import LastOnlineItem from "@/features/model/components/LastOnlineItem";
import { ModelExperience } from "@/features/model/components/ModelExperience";
import { SaveModelButton } from "@/features/model/components/modeList/SaveModelButton";
import { PersonalInfo } from "@/features/model/components/profile/PersonalInfo";
import { RatingsCard } from "@/features/model/components/profile/RatingsCard";
import { ModelPhotoList } from "@/features/modelPhoto/components/ModelPhotoList";
import { EditModelAlbumFormProvider } from "@/features/modelPhoto/contexts/useEditModelAlbumFormContext";
import { AccessTypeEnum, useModelQuery } from "@/generated/graphql-hooks";
import { getPublicConfig } from "@/helpers/getPublicConfig";
import SaveModelIcon from "@/icons/save_model.svg";
import { getSsrModel } from "@/server/graphql/ssr/getSsrModel";
import { getImageUrl } from "@/services/cloudinary/getImageUrl";
import { prisma } from "@/services/prisma";

const { features } = getPublicConfig();

const ModelProfilePage = () => {
  const { trackEvent } = useAnalytics();
  const { user } = useUserContext();
  const { query, isFallback, push } = useRouter();
  const { data: { model } = {} } = useModelQuery({
    where: { id: query.modelId as string },
  });

  const modelLogoUrl = model?.avatarPublicId
    ? getImageUrl(model.avatarPublicId, {
        size: features.common.seoOgImageSize,
      })
    : null;

  const seoProps = useSeoProps({
    title: `${model?.username} KOL個人資料`,
    description: model?.description,
    openGraph: {
      images: modelLogoUrl
        ? [
            {
              url: modelLogoUrl,
              width: features.common.seoOgImageSize,
              height: features.common.seoOgImageSize,
              alt: `${model.username} 商戶資料`,
              type: "image/jpeg",
            },
          ]
        : undefined,
    },
  });

  // @todo add loading state
  if (isFallback) return null;
  if (!model) return <NotFoundError />;
  if (!!model.isDeleted)
    return (
      <NotFoundError
        title="用戶已刪除"
        description="無法查看已刪除用戶的資料"
      />
    );

  const onEditPublicAlbum = () => {
    push("/kol/editPublicAlbum");
    trackEvent(ModelEventEnum.CLICK_EDIT_PUBLIC_ALBUM);
  };

  return (
    <>
      <NextSeo {...seoProps} />

      <Container
        className={clsx(
          "pb-10 sm:pt-8",
          !model.hasAlbum && !model.experience && "min-h-screen",
        )}
      >
        <div className="py-2">
          <Breadcrumbs
            items={[
              { title: "首頁", href: "/" },
              { title: "KOL", href: "/kol" },
              {
                title: model.username,
                href: `/kol/${model.id}/${model.username}`,
              },
            ]}
          />
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="-mx-4 space-y-2 sm:mx-0">
            <div className="relative aspect-square w-full bg-slate-200 sm:rounded-xl">
              <CloudinaryImage
                alt={model.username}
                className="sm:rounded-xl"
                publicId={model.avatarPublicId}
              />

              <IGFollowerNumberItem
                instagramFollowerCount={model.instagramFollowerCount}
              />
            </div>

            <LastOnlineItem lastOnlinedAt={model.lastOnlinedAt} />
          </div>
          <div className="space-y-3 sm:space-y-5">
            <div className="flex items-center justify-between gap-1">
              <div className="text-2xl text-gray-900 sm:text-3xl">
                {model.username}
              </div>

              <SaveModelButton
                isSaved={model.isSaved}
                modelId={model.id}
                icon={
                  <SaveModelIcon
                    className={clsx(
                      model.isSaved ? "text-gray-700" : "text-gray-200",
                      "h-7 w-7",
                    )}
                  />
                }
              />
            </div>
            {!!model.description && (
              <EllipsisText
                className="whitespace-pre-wrap break-words text-base font-normal"
                text={model.description}
              />
            )}
            <RatingsCard modelUserId={model.userId} />
            <PersonalInfo model={model} />

            {user?.roleData?.__typename === "Model" &&
              user.roleData.id === query.modelId && (
                <Button onClick={onEditPublicAlbum}>編輯公開相簿</Button>
              )}
          </div>
        </div>
      </Container>

      {(!!model.hasAlbum || !!model.experience) && (
        <div className="w-full flex-1 bg-white">
          <Container className="my-8 space-y-10 bg-white sm:flex sm:space-x-3 sm:space-y-0">
            <div className="flex-1">
              <EditModelAlbumFormProvider>
                <ModelPhotoList
                  model={model}
                  previewPrivate
                  accessType={AccessTypeEnum.Public}
                />
              </EditModelAlbumFormProvider>
            </div>
            <div className="sm:w-1/3">
              <ModelExperience experience={model.experience} />
            </div>
          </Container>
        </div>
      )}
    </>
  );
};

export const getStaticProps = async (
  context: GetStaticPropsContext<Record<"modelId" | "modelName", string>>,
) => {
  const queryClient = new QueryClient();
  const variables = { where: { id: context.params.modelId as string } };

  await queryClient.prefetchQuery(
    ["Model", variables],
    async () => await getSsrModel({ variables }),
  );

  const dehydratedState = dehydrate(queryClient);

  return {
    props: { dehydratedState },
    revalidate: 10,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const models = await prisma.model.findMany();
  return {
    paths: models.map((model) => ({
      params: { modelId: model.id, modelName: model.username },
    })),
    fallback: "blocking",
  };
};

export default ModelProfilePage;

ModelProfilePage.getLayout = function getLayout(page) {
  return <SiteLayout>{page}</SiteLayout>;
};
